import { useEffect, useMemo, useState } from 'react';
import { Eye, Tag, X } from 'lucide-react';
import type { ArtSelectionItem } from '../types';
import {
  buildPreviewLabel,
  getPartOptions,
  getSideOptions,
  normalizeUiState,
  MOLDE_V3_PIECES,
  type MoldeV3UiPiece,
  type MoldeV3UiState,
} from './catalog';

export type ArtSlotOption = {
  value: string;
  label: string;
  kind: 'estrutural';
};

type ArtConfigSheetProps = {
  item: ArtSelectionItem | null;
  open: boolean;
  busy: boolean;
  slotOptions: ArtSlotOption[];
  onClose: () => void;
  onFocus: (item: ArtSelectionItem) => Promise<void> | void;
  onApply: (slotId: string) => Promise<void>;
  onClear: () => Promise<void>;
};

type DraftKind = '' | 'estrutural' | 'emblema';

type DraftState = {
  kind: DraftKind;
  piece: MoldeV3UiPiece;
  part: string;
  side: string;
  index: string;
};

type ParsedArtSlotOption = {
  value: string;
  label: string;
  kind: 'estrutural';
  piece: string;
  part: string;
  side: string;
};

const DEFAULT_DRAFT: DraftState = {
  kind: 'estrutural',
  piece: 'corpo',
  part: 'frente',
  side: '',
  index: '1',
};

const PART_LABELS: Record<string, string> = {
  frente: 'Frente',
  costas: 'Costas',
  inteira: 'Inteira',
  curta: 'Curta',
  longa: 'Longa',
  raglan: 'Raglan',
  punho: 'Punho',
  barra_corpo: 'Barra do corpo',
  barra_manga: 'Barra da manga',
  barra_short: 'Barra do short',
  lateral: 'Lateral',
  faixa: 'Faixa',
  peitilho: 'Peitilho',
  recorte_frente: 'Recorte frente',
  recorte_costas: 'Recorte costas',
  cos: 'Cos',
  entrepernas: 'Entrepernas',
  O: 'O',
  V: 'V',
  U: 'U',
  polo: 'Polo',
  vies: 'Vies',
};

const SIDE_LABELS: Record<string, string> = {
  '': 'Unico',
  dir: 'Direito',
  esq: 'Esquerdo',
};

function getStateLabel(item: ArtSelectionItem) {
  switch (item.state) {
    case 'marcado':
      return 'Marcado';
    case 'conflito':
      return 'Conflito';
    default:
      return 'Pendente';
  }
}

function formatLabel(value: string) {
  if (!value) return 'Unico';
  const piece = MOLDE_V3_PIECES.find((item) => item.value === value);
  if (piece) return piece.label;
  if (PART_LABELS[value]) return PART_LABELS[value];
  if (SIDE_LABELS[value]) return SIDE_LABELS[value];
  return value
    .split('_')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

function parseArtSlotOption(option: ArtSlotOption): ParsedArtSlotOption {
  const [piece = 'corpo', part = 'frente', side = ''] = option.value.split('.');
  return {
    ...option,
    piece,
    part,
    side,
  };
}

function buildDraftFromSlotId(slotId: string, options: ParsedArtSlotOption[], restrictStructural: boolean): DraftState {
  if (!slotId) {
    return { ...DEFAULT_DRAFT };
  }

  if (slotId.startsWith('emblema.')) {
    const [, index = '1'] = slotId.split('.');
    return { ...DEFAULT_DRAFT, kind: 'emblema', index };
  }

  if (restrictStructural) {
    const parsed = options.find((option) => option.value === slotId);
    if (parsed) {
      return {
        ...DEFAULT_DRAFT,
        kind: 'estrutural',
        piece: (parsed.piece as MoldeV3UiPiece) || 'corpo',
        part: parsed.part || 'frente',
        side: parsed.side || '',
      };
    }
  }

  const [piece = 'corpo', part = 'frente', side = ''] = slotId.split('.');
  return {
    ...DEFAULT_DRAFT,
    kind: 'estrutural',
    piece: piece as MoldeV3UiPiece,
    part,
    side,
  };
}

function normalizeStructuralDraft(draft: DraftState): DraftState {
  const normalized = normalizeUiState({
    mode: 'estrutural',
    piece: draft.piece || 'corpo',
    part: draft.part || 'frente',
    side: draft.side || '',
    placeholder: 'nome_completo',
  } as MoldeV3UiState);

  return {
    ...DEFAULT_DRAFT,
    kind: 'estrutural',
    piece: normalized.piece,
    part: normalized.part,
    side: normalized.side,
  };
}

function normalizeRestrictedStructuralDraft(draft: DraftState, options: ParsedArtSlotOption[]): DraftState {
  if (!options.length) {
    return normalizeStructuralDraft(draft);
  }

  const pieces = Array.from(new Set(options.map((option) => option.piece).filter(Boolean)));
  const piece = pieces.includes(draft.piece) ? draft.piece : ((pieces[0] as MoldeV3UiPiece) || 'corpo');

  const parts = Array.from(
    new Set(options.filter((option) => option.piece === piece).map((option) => option.part).filter(Boolean))
  );
  const part = parts.includes(draft.part) ? draft.part : parts[0] || 'frente';

  const sides = Array.from(
    new Set(options.filter((option) => option.piece === piece && option.part === part).map((option) => option.side))
  );
  const side = sides.includes(draft.side) ? draft.side : sides[0] || '';

  return {
    ...DEFAULT_DRAFT,
    kind: 'estrutural',
    piece,
    part,
    side,
  };
}

function normalizeDraft(draft: DraftState, options: ParsedArtSlotOption[], restrictStructural: boolean): DraftState {
  if (draft.kind === 'emblema') {
    return {
      ...DEFAULT_DRAFT,
      kind: 'emblema',
      index: draft.index || '1',
    };
  }

  return restrictStructural
    ? normalizeRestrictedStructuralDraft(draft, options)
    : normalizeStructuralDraft({ ...draft, kind: 'estrutural' });
}

function resolveSlotId(draft: DraftState, options: ParsedArtSlotOption[], restrictStructural: boolean) {
  if (!draft.kind) return '';

  if (draft.kind === 'emblema') {
    return `emblema.${draft.index || '1'}`;
  }

  if (restrictStructural) {
    return (
      options.find(
        (option) =>
          option.piece === draft.piece &&
          option.part === draft.part &&
          option.side === draft.side
      )?.value || ''
    );
  }

  return buildPreviewLabel({
    mode: 'estrutural',
    piece: draft.piece,
    part: draft.part,
    side: draft.side,
    placeholder: 'nome_completo',
  } as MoldeV3UiState);
}

export function ArtConfigSheet({
  item,
  open,
  busy,
  slotOptions,
  onClose,
  onFocus,
  onApply,
  onClear,
}: ArtConfigSheetProps) {
  const [draft, setDraft] = useState<DraftState>(DEFAULT_DRAFT);
  const [isRendered, setIsRendered] = useState(open);
  const [isVisible, setIsVisible] = useState(open);

  const parsedStructuralOptions = useMemo(() => slotOptions.map(parseArtSlotOption), [slotOptions]);
  const restrictStructural = parsedStructuralOptions.length > 0;
  const isAutoDetectedText = Boolean(
    item &&
      item.slotKind === 'texto' &&
      (item.detectedTextTokens?.length ?? 0) > 0
  );

  useEffect(() => {
    if (!item) {
      setDraft(DEFAULT_DRAFT);
      return;
    }

    const nextDraft = buildDraftFromSlotId(item.slotId || '', parsedStructuralOptions, restrictStructural);
    setDraft(normalizeDraft(nextDraft, parsedStructuralOptions, restrictStructural));
  }, [item, parsedStructuralOptions, restrictStructural]);

  useEffect(() => {
    if (open && item) {
      setIsRendered(true);
      const rafId = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(rafId);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => setIsRendered(false), 180);
    return () => window.clearTimeout(timeoutId);
  }, [item, open]);

  const normalizedDraft = useMemo(
    () => normalizeDraft(draft, parsedStructuralOptions, restrictStructural),
    [draft, parsedStructuralOptions, restrictStructural]
  );

  const pieceOptions = useMemo(() => {
    if (restrictStructural) {
      return Array.from(
        new Set(parsedStructuralOptions.map((option) => option.piece).filter(Boolean))
      ).map((piece) => ({ value: piece, label: formatLabel(piece) }));
    }

    return MOLDE_V3_PIECES.map((piece) => ({ value: piece.value, label: piece.label }));
  }, [parsedStructuralOptions, restrictStructural]);

  const partOptions = useMemo(() => {
    if (restrictStructural) {
      return Array.from(
        new Set(
          parsedStructuralOptions
            .filter((option) => option.piece === normalizedDraft.piece)
            .map((option) => option.part)
            .filter(Boolean)
        )
      ).map((part) => ({ value: part, label: formatLabel(part) }));
    }

    return getPartOptions(normalizedDraft.piece).map((option) => ({ value: option.value, label: option.label }));
  }, [normalizedDraft.piece, parsedStructuralOptions, restrictStructural]);

  const sideOptions = useMemo(() => {
    if (restrictStructural) {
      return Array.from(
        new Set(
          parsedStructuralOptions
            .filter(
              (option) =>
                option.piece === normalizedDraft.piece &&
                option.part === normalizedDraft.part
            )
            .map((option) => option.side)
        )
      ).map((side) => ({ value: side, label: formatLabel(side) }));
    }

    return getSideOptions(normalizedDraft.piece, normalizedDraft.part).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, [normalizedDraft.part, normalizedDraft.piece, parsedStructuralOptions, restrictStructural]);

  const indexOptions = useMemo(
    () => Array.from({ length: 6 }, (_, index) => ({ value: String(index + 1), label: `#${index + 1}` })),
    []
  );

  const selectedSlotId = useMemo(
    () => resolveSlotId(normalizedDraft, parsedStructuralOptions, restrictStructural),
    [normalizedDraft, parsedStructuralOptions, restrictStructural]
  );

  const selectedLabel = useMemo(() => {
    if (!selectedSlotId) {
      return 'Nenhum destino selecionado';
    }

    if (normalizedDraft.kind === 'emblema') {
      return `Emblema #${normalizedDraft.index}`;
    }

    return buildPreviewLabel({
      mode: 'estrutural',
      piece: normalizedDraft.piece,
      part: normalizedDraft.part,
      side: normalizedDraft.side,
      placeholder: 'nome_completo',
    } as MoldeV3UiState);
  }, [normalizedDraft, selectedSlotId]);

  if (!isRendered || !item) return null;

  return (
    <div
      className={`absolute inset-0 z-30 flex items-end bg-navy-950/70 backdrop-blur-sm transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <button aria-label="Fechar configuracao da arte" onClick={onClose} className="absolute inset-0" />

      <div
        className={`relative z-10 flex max-h-[78%] w-full flex-col overflow-hidden rounded-t-[22px] border border-navy-700 bg-navy-900 shadow-2xl transition-transform duration-200 ${
          isVisible ? 'translate-y-0' : 'translate-y-8'
        }`}
      >
        <div className="border-b border-navy-700 px-3 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.2em] text-navy-400">Definir Artes</div>
              <h3 className="mt-0.5 text-base font-semibold text-cyan-300">{item.shapeName}</h3>
              <div className="mt-0.5 text-[11px] text-navy-300">
                {item.layerName || 'Sem camada'} - #{item.staticId} - {item.shapeType}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-navy-600 bg-navy-800 p-1.5 text-navy-200 transition-colors hover:bg-navy-700"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
          <div className="rounded-xl border border-cyan-700/30 bg-cyan-900/10 px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.18em] text-cyan-300">Status atual</div>
            <div className="mt-0.5 text-[13px] font-semibold text-white">{getStateLabel(item)}</div>
            <div className="mt-1 text-[11px] text-navy-200">{item.slotId || 'Objeto ainda sem destino definido.'}</div>
          </div>

          {!restrictStructural && (
            <div className="rounded-xl border border-yellow-700/30 bg-yellow-900/10 px-3 py-2.5 text-[11px] text-yellow-100">
              Sem destinos estruturais lidos ainda. Voce pode marcar a arte e validar depois.
            </div>
          )}

          {item.problems?.length ? (
            <div className="rounded-xl border border-red-700/30 bg-red-900/10 px-3 py-2.5 text-[11px] text-red-200">
              {item.problems.join(' ')}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void onFocus(item)}
              className="inline-flex items-center gap-2 rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[11px] font-medium text-navy-100 transition-colors hover:bg-navy-700"
            >
              <Eye size={13} />
              Focar no Corel
            </button>
          </div>

          <div className="grid gap-3">
            {isAutoDetectedText ? (
              <div className="space-y-2 rounded-xl border border-cyan-700/20 bg-cyan-900/10 px-3 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  Texto automatico
                </div>
                <div className="text-[11px] leading-5 text-navy-200">
                  Esse shape ja foi reconhecido pelo proprio conteudo. Sempre que encontrar <span className="font-semibold text-white">$</span>, <span className="font-semibold text-white">%</span>, <span className="font-semibold text-white">#</span> ou <span className="font-semibold text-white">&amp;</span>, o Aplicar Arte trata o objeto como texto dinamico sem precisar marcar manualmente.
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  {[
                    ['$ = nome', '$'],
                    ['% = sobrenome', '%'],
                    ['# = numero', '#'],
                    ['& = etiqueta/tamanho', '&'],
                  ].map(([label, token]) => (
                    <span
                      key={token}
                      className={`rounded-full border px-2 py-0.5 ${
                        item.detectedTextTokens?.includes(token)
                          ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100'
                          : 'border-navy-600 bg-navy-800 text-navy-200'
                      }`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
                {item.textValue ? (
                  <div className="rounded-lg border border-navy-700 bg-navy-950/60 px-3 py-2 text-[11px] text-navy-200">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy-400">
                      Conteudo atual
                    </div>
                    <div className="whitespace-pre-wrap break-words">{item.textValue}</div>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                    Tipo
                  </label>
                  <select
                    value={normalizedDraft.kind}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        kind: event.target.value as DraftKind,
                      }))
                    }
                    className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-cyan-400"
                  >
                    <option value="estrutural">Estrutural</option>
                    <option value="emblema">Emblema</option>
                  </select>
                </div>

                {item.isText && (
                  <div className="rounded-xl border border-yellow-700/30 bg-yellow-900/10 px-3 py-2.5 text-[11px] text-yellow-100">
                    Texto agora entra automatico quando o conteudo tiver $ % # ou &. Se este objeto for decorativo, deixe sem marcar ou use estrutural/emblema apenas quando fizer sentido.
                  </div>
                )}

                {normalizedDraft.kind === 'estrutural' && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                        Peca
                      </label>
                      <select
                        value={normalizedDraft.piece}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            piece: event.target.value as MoldeV3UiPiece,
                            part: '',
                            side: '',
                          }))
                        }
                        className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-cyan-400"
                      >
                        {pieceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                        Parte
                      </label>
                      <select
                        value={normalizedDraft.part}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            part: event.target.value,
                            side: '',
                          }))
                        }
                        className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-cyan-400"
                      >
                        {partOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                        Lado
                      </label>
                      <select
                        value={normalizedDraft.side}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            side: event.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-cyan-400"
                      >
                        {sideOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {normalizedDraft.kind === 'emblema' && (
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                      Indice
                    </label>
                    <select
                      value={normalizedDraft.index}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          index: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-cyan-400"
                    >
                      {indexOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>

          {!isAutoDetectedText && (
            <div className="rounded-xl border border-navy-700 bg-navy-950/70 px-3 py-2.5">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-yellow-400">
                <Tag size={12} />
                Vai gravar
              </div>
              <div className="mt-1 text-[13px] font-semibold text-white">{selectedLabel}</div>
              {selectedSlotId ? <div className="mt-1 text-[11px] text-navy-300">{selectedSlotId}</div> : null}
            </div>
          )}
        </div>

        {isAutoDetectedText ? (
          <div className="border-t border-navy-700 px-3 py-3">
            <button
              onClick={onClose}
              className="w-full rounded-xl bg-yellow-500 px-3 py-2 text-sm font-semibold text-navy-950 transition-colors hover:bg-yellow-400"
            >
              Fechar
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-[auto_1fr] gap-2 border-t border-navy-700 px-3 py-3">
            <button
              onClick={() => void onClear()}
              disabled={busy}
              className="rounded-xl border border-red-700/40 bg-red-900/10 px-3 py-2 text-sm font-semibold text-red-200 transition-colors hover:bg-red-900/20 disabled:opacity-60"
            >
              Limpar
            </button>
            <button
              onClick={() => void onApply(selectedSlotId)}
              disabled={busy || !selectedSlotId}
              className="rounded-xl bg-yellow-500 px-3 py-2 text-sm font-semibold text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-60"
            >
              Aplicar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
